import React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'reducers';
import { RouteComponentProps, Link } from 'react-router-dom';
import { Globals, Constants, Colors, Print, checkPermission } from 'utils';
import { Table, Pagination, Tooltip, Input, DatePicker, Select, Modal } from 'components';
import type { SortableColumn } from 'components/table';
import {
  CleanIcon,
  TrashIcon,
  EditIcon,
  AddIcon,
  DownloadExcelIcon,
  ViewIcon,
  CloseRedIcon,
  CheckGreenCircle,
  ToggleGreen,
  ToggleRed,
} from 'assets/icons';
import ModalCreate from './modal-create';
import { LawyerService, UserService } from 'services';
import { TABS, TabsTop } from '../clients/clients';
import moment from 'moment';
import ModalView from '../modal-view';

declare var window: any;

interface Props extends RootState, RouteComponentProps {
  dispatch: any;
}

const INITIAL_STATE = {
  search: '',
  date: '',
};

const SORTABLE_COLUMNS: SortableColumn[] = [
  { index: 0, key: 'created_at', type: 'date' },
  { index: 1, key: 'full_name', type: 'string' },
  { index: 3, key: 'lawyer_document', type: 'string' },
  { index: 5, key: 'email', type: 'string' },
  {
    index: 6,
    key: 'membership_name',
    type: 'string',
    accessor: (item: any) => (item.plan_user?.length ? item.plan_user[0].membership?.name : null),
  },
  { index: 9, key: 'internal', type: 'number' },
  { index: 11, key: 'status', type: 'number' },
];

class AdminLawyers extends React.Component<Props> {
  timer: any = undefined;

  state = {
    header: [
      'Fecha de Registro',
      'Nombre',
      'C.I.',
      'Nº de Inpre',
      'Teléfono',
      'Correo',
      'Membresía',
      'Fecha de membresía',
      'Ranking',
      'Interno',
      'Verificación',
      'Estatus',
      'Sin comisión',
      'Acciones',
    ],
    form: INITIAL_STATE,
    data: [],
    originalData: [] as any[],
    page: 1,
    last_page: 1,
    visible: false,
    item: null,
    visible_view: false,
  };

  componentDidMount() {
    this.props.dispatch({
      type: 'SET_HEADER',
      payload: {
        title: 'Abogados',
        onBack: null,
      },
    });

    this.props.dispatch({
      type: 'SET_SUBHEADER',
      payload: {
        title: '',
      },
    });

    this.load();
  }

  load = async (withoutLoading: boolean = false) => {
    const res: any = await LawyerService.admin.get({
      withoutLoading,
      page: this.state.page,
      ...this.state.form,
      date: this.state.form.date && moment(this.state.form.date).format('YYYY-MM-DD'),
    });
    this.setState({
      data: res.lawyers.rows,
      originalData: [...res.lawyers.rows],
      last_page: res.lawyers.count,
    });
  };

  reset = () => {
    this.setState(
      {
        form: INITIAL_STATE,
        page: 1,
      },
      () => this.load(true),
    );
  };

  edit = (e: any, item: any) => {
    e.preventDefault();
    this.setState({
      visible: true,
      item,
    });
  };

  delete = (e: any, item: any) => {
    e.preventDefault();
    Globals.confirm('¿Desea eliminar el abogado?', async () => {
      await LawyerService.admin.delete({
        user_id: item.id,
      });
      this.load(true);
    });
  };

  change = (e: any, callback = () => {}) => {
    this.setState(
      {
        page: 1,
        form: {
          ...this.state.form,
          [e.target.name]: e.target.value,
        },
      },
      callback,
    );
  };

  add = () => {
    this.setState({
      visible: true,
    });
  };

  onClose = () => {
    this.setState(
      {
        visible: false,
        item: null,
      },
      () => this.load(true),
    );
  };

  download = async (segment?: 'MQL' | 'SAL' | 'SQL') => {
    // Un solo método maneja los 4 botones de descarga (completo + MQL/SAL/SQL)
    // -- solo cambia qué método del servicio se invoca, el resto (apertura
    // del archivo, manejo del webview de iOS) es idéntico para los cuatro.
    const serviceMethod =
      segment === 'MQL'
        ? LawyerService.admin.downloadMQL
        : segment === 'SAL'
          ? LawyerService.admin.downloadSAL
          : segment === 'SQL'
            ? LawyerService.admin.downloadSQL
            : LawyerService.admin.download;

    const res: any = await serviceMethod({
      ...this.state.form,
    });
    if (window.webkit && window.webkit.messageHandlers) {
      const message = 'open_url';
      const sendToNative = {
        message,
        url: Constants.BASE_STORAGE + res.url,
      };
      const stringifiedMessageObj = JSON.stringify(sendToNative);
      window.webkit.messageHandlers.cordova_iab.postMessage(stringifiedMessageObj);
    } else {
      Print(Constants.BASE_STORAGE + res.url);
    }
  };

  onCloseView = () => {
    this.setState({
      visible_view: false,
      item: null,
    });
  };

  view = (e: any, item: any) => {
    e.preventDefault();
    this.setState({
      visible_view: true,
      item,
    });
  };

  changeStatus = (e: any, item: any, action: number) => {
    e.preventDefault();
    let message = '';
    let message_confirm = '';

    switch (action) {
      case Constants.VERIFIED.VERIFIED:
        message = '¿Desea verificar el abogado?';
        message_confirm = 'Se ha verificado el abogado';
        break;

      case Constants.VERIFIED.REJECTED:
        message = '¿Desea rechazar el abogado?';
        message_confirm = 'Se ha rechazado el abogado';
        break;
    }

    Globals.confirm(message, async () => {
      await LawyerService.admin.changeStatus({
        lawyer_id: item?.id,
        action,
      });
      this.load();
      Globals.showSuccess(message_confirm);
    });
  };

  changeActive = (e: any, item: any) => {
    e.preventDefault();
    let message = '';
    let message_confirm = '';

    switch (item.status) {
      case Constants.USER_STATUS.ACTIVE:
        message = '¿Desea suspender el usuario?';
        message_confirm = 'Se ha suspendido el usuario';
        break;

      case Constants.USER_STATUS.INACTIVE:
        message = '¿Desea activar el usuario?';
        message_confirm = 'Se ha activado el usuario';
        break;
    }

    Globals.confirm(message, async () => {
      await UserService.admin.changeActive({
        user_id: item?.id,
      });
      this.load();
      Globals.showSuccess(message_confirm);
    });
  };

  render() {
    const { visible, visible_view } = this.state;

    return (
      <div id='admin-lawyers'>
        <Modal
          className='modal-create-lawyer'
          visible={visible}
          title={`${this.state.item ? 'Editar' : 'Crear'} abogado`}
          onClose={() => this.onClose()}
        >
          <ModalCreate item={this.state.item} onClose={() => this.onClose()} />
        </Modal>

        <Modal
          className='modal-view-user'
          visible={visible_view}
          title={''}
          onClose={() => this.onCloseView()}
        >
          <ModalView
            edit={() => {
              this.setState({
                visible_view: false,
                visible: true,
              });
            }}
            item={this.state.item}
            reload={() => this.load(true)}
            onClose={() => this.onCloseView()}
          />
        </Modal>

        <TabsTop history={this.props.history} active={TABS.LAWYERS} />

        <div className='row row-filter'>
          <div className='col-md-12'>
            <div className='row'>
              <div className='col-md-4'>
                <Input
                  value={this.state.form.search}
                  name='search'
                  placeholder='Buscar'
                  label='Nombre, correo o cédula'
                  className='input-table'
                  onSubmit={() => this.load()}
                  onChange={(e: any) => {
                    clearTimeout(this.timer);
                    this.timer = setTimeout(() => {
                      this.load(true);
                    }, 1000);
                    this.change(e);
                  }}
                />
              </div>
              <div className='col-md-4'>
                <DatePicker
                  label='Fecha de Registro'
                  placeholder='Fecha'
                  maxDate={moment().toDate()}
                  onChange={(text: string) => {
                    this.change(
                      {
                        target: {
                          value: text,
                          name: 'date',
                        },
                      },
                      () => this.load(true),
                    );
                  }}
                  value={this.state.form.date}
                />
              </div>
              <div className='col-md-1'>
                {this.state.form != INITIAL_STATE && (
                  <button className='btn btn-clean' onClick={() => this.reset()}>
                    <img src={CleanIcon} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <Table
          title='Abogados'
          data={this.state.data.length}
          header={this.state.header}
          sortableColumns={SORTABLE_COLUMNS}
          rawData={this.state.originalData}
          onSort={(sorted) => this.setState({ data: sorted })}
          titleRight={
            <>
              {this.state.data.length > 0 && (
                <div className='container-btns-table'>
                  <button className='btn download-excel' onClick={() => this.download()}>
                    <img src={DownloadExcelIcon} />
                    <p>Descargar Excel</p>
                  </button>
                  <button className='btn download-excel' onClick={() => this.download('MQL')}>
                    <img src={DownloadExcelIcon} />
                    <p>Descargar MQL</p>
                  </button>
                  <button className='btn download-excel' onClick={() => this.download('SAL')}>
                    <img src={DownloadExcelIcon} />
                    <p>Descargar SAL</p>
                  </button>
                  <button className='btn download-excel' onClick={() => this.download('SQL')}>
                    <img src={DownloadExcelIcon} />
                    <p>Descargar SQL</p>
                  </button>
                </div>
              )}
            </>
          }
        >
          {this.state.data.map((i: any, index: number) => {
            const membershipDate = i.plan_user
              ? !!i.plan_user.length
                ? new Intl.DateTimeFormat('es-ES').format(
                    new Date(i.plan_user[0].membership?.created_at),
                  )
                : '-'
              : '-';

            const membershipName = i.plan_user
              ? !!i.plan_user.length
                ? i.plan_user[0].membership?.name
                : '-'
              : '-';
            return (
              <tr key={index}>
                <td>{moment(i.created_at).format('DD/MM/YYYY')}</td>
                <td>{i.full_name}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{i.full_document}</td>
                <td>{i.lawyer_document || '-'}</td>
                <td>{i.full_phone}</td>
                <td>{i.email}</td>
                <td>{membershipName}</td>
                <td>{membershipDate}</td>
                <td>{i?.avgRating ? Globals.formatMiles(i?.avgRating, true, '') : '-'}</td>
                <td>{i?.internal_text}</td>
                <td>{i.verified_text}</td>
                <td>{i.status_text}</td>
                <td>
                  {i.platform_fee_exempt ? (
                    <span
                      style={{
                        backgroundColor: Colors.green,
                        color: '#fff',
                        borderRadius: '8px',
                        padding: '2px 8px',
                        fontSize: '11px',
                        fontWeight: 700,
                        display: 'inline-block',
                      }}
                    >
                      Sí
                    </span>
                  ) : (
                    '-'
                  )}
                </td>
                <td className=''>
                  <Tooltip title='Ver'>
                    <a className='link-icon' href='#' onClick={(e: any) => this.view(e, i)}>
                      <img src={ViewIcon} />
                    </a>
                  </Tooltip>
                  {checkPermission(Constants.MODULES.USERS, 'write') && (
                    <Tooltip title='Editar'>
                      <a className='link-icon' href='#' onClick={(e: any) => this.edit(e, i)}>
                        <img src={EditIcon} />
                      </a>
                    </Tooltip>
                  )}
                  {checkPermission(Constants.MODULES.USERS, 'delete') && (
                    <Tooltip title='Eliminar'>
                      <a className='link-icon' href='#' onClick={(e: any) => this.delete(e, i)}>
                        <img src={TrashIcon} />
                      </a>
                    </Tooltip>
                  )}
                  {(i.verified == Constants.VERIFIED.PENDING ||
                    i.verified == Constants.VERIFIED.IN_PROCESS) && (
                    <>
                      <Tooltip title='Verificar'>
                        <a
                          className='link-icon'
                          href='#'
                          onClick={(e: any) => this.changeStatus(e, i, Constants.VERIFIED.VERIFIED)}
                        >
                          <img src={CheckGreenCircle} />
                        </a>
                      </Tooltip>
                      <Tooltip title='Rechazar'>
                        <a
                          className='link-icon'
                          href='#'
                          onClick={(e: any) => this.changeStatus(e, i, Constants.VERIFIED.REJECTED)}
                        >
                          <img src={CloseRedIcon} />
                        </a>
                      </Tooltip>
                    </>
                  )}
                  {i.verified == Constants.VERIFIED.VERIFIED &&
                    checkPermission(Constants.MODULES.USERS, 'write') && (
                      <Tooltip
                        title={i.status == Constants.USER_STATUS.ACTIVE ? 'Suspender' : 'Activar'}
                      >
                        <a
                          className='link-icon link-status'
                          href='#'
                          onClick={(e: any) => this.changeActive(e, i)}
                        >
                          <img
                            src={i.status == Constants.USER_STATUS.ACTIVE ? ToggleGreen : ToggleRed}
                          />
                        </a>
                      </Tooltip>
                    )}
                </td>
              </tr>
            );
          })}
        </Table>

        <Pagination
          pages={this.state.last_page}
          active={this.state.page}
          onChange={async (page: number) => {
            await this.setState({
              page: page,
            });
            this.load();
          }}
        />
      </div>
    );
  }
}

export default connect((state: RootState) => {
  return {
    user: state.user,
  };
})(AdminLawyers);
